import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { agentsApi, clientsApi, dataApi } from '../services/api';
import './DataViewer.css';

const DataViewer = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialAgentId = queryParams.get('agent');

  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [extractedData, setExtractedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(initialAgentId || '');

  // Load initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Get clients
        const clientsResponse = await clientsApi.getClients();
        setClients(clientsResponse.clients || []);

        // Get all agents initially, or filter by client if selected
        const agentsResponse = await agentsApi.getAgents(selectedClient || null);
        setAgents(agentsResponse.agents || []);

        // If agent is selected (from URL or state), fetch its data
        if (selectedAgent) {
          const dataResponse = await dataApi.getDataByAgent(selectedAgent);
          setExtractedData(dataResponse.data || []);
        } else {
          setExtractedData([]);
        }

        setError('');
      } catch (err) {
        setError('Failed to load data. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedClient, selectedAgent, initialAgentId]);

  // Handle client change
  const handleClientChange = async (e) => {
    const clientId = e.target.value;
    setSelectedClient(clientId);
    setSelectedAgent(''); // Reset agent selection when client changes

    try {
      setLoading(true);
      
      // Update agents list for selected client
      const agentsResponse = await agentsApi.getAgents(clientId || null);
      setAgents(agentsResponse.agents || []);
      
      // Clear data since no agent is selected
      setExtractedData([]);
      
      setError('');
    } catch (err) {
      setError('Failed to filter agents. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle agent change
  const handleAgentChange = async (e) => {
    const agentId = e.target.value;
    setSelectedAgent(agentId);

    if (!agentId) {
      setExtractedData([]);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch data for selected agent
      const dataResponse = await dataApi.getDataByAgent(agentId);
      setExtractedData(dataResponse.data || []);
      
      setError('');
    } catch (err) {
      setError('Failed to load agent data. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Find agent name by ID
  const getAgentName = (agentId) => {
    const agent = agents.find(a => a.id === parseInt(agentId));
    return agent ? agent.name : 'Unknown Agent';
  };

  // Get field keys for table headers
  const getFieldKeys = () => {
    if (extractedData.length === 0) return [];
    
    // Get all unique field keys across all entries
    const keys = new Set();
    extractedData.forEach(entry => {
      Object.keys(entry.fields).forEach(key => keys.add(key));
    });
    
    return Array.from(keys);
  };

  const fieldKeys = getFieldKeys();

  return (
    <div>
      <div className="header">
        <h1>Extracted Data</h1>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="card">
        <div className="filter-section">
          <div className="filter-group">
            <label htmlFor="client-filter">Client:</label>
            <select
              id="client-filter"
              value={selectedClient}
              onChange={handleClientChange}
              className="form-control"
            >
              <option value="">All Clients</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label htmlFor="agent-filter">Agent:</label>
            <select
              id="agent-filter"
              value={selectedAgent}
              onChange={handleAgentChange}
              className="form-control"
            >
              <option value="">Select Agent</option>
              {agents.map(agent => (
                <option key={agent.id} value={agent.id}>{agent.name}</option>
              ))}
            </select>
          </div>
        </div>
        
        {loading ? (
          <div className="loading">Loading data...</div>
        ) : !selectedAgent ? (
          <div className="empty-state">
            <p>Select an agent to view extracted data</p>
          </div>
        ) : extractedData.length === 0 ? (
          <div className="empty-state">
            <p>No data available for this agent</p>
          </div>
        ) : (
          <div className="data-table-container">
            <h3>Data for {getAgentName(selectedAgent)}</h3>
            <div className="data-count">
              {extractedData.length} records found
            </div>
            
            <div className="table-wrapper">
              <table className="table data-table">
                <thead>
                  <tr>
                    <th>Conversation ID</th>
                    <th>Timestamp</th>
                    {fieldKeys.map(key => (
                      <th key={key}>{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {extractedData.map((entry, index) => (
                    <tr key={`${entry.conversation_id}-${index}`}>
                      <td className="id-cell">{entry.conversation_id}</td>
                      <td>{formatDate(entry.timestamp)}</td>
                      {fieldKeys.map(key => (
                        <td key={key}>{entry.fields[key] || '-'}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataViewer;
